// frontend/src/pages/builder/FormatPanel.jsx
// Phase 3 step 7b-2c: the Visual panel's Format section -- the chart's title,
// number format (with currency and decimals) and value labels. Rules and the
// payload live in formatSettings.js; drawing lives in charts/format.js and
// charts/echartsOption.js, shared with the embed.
import React from 'react';
import { PanelHint, PanelLabel } from '@/components/ui/panel.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.jsx';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group.jsx';
import {
  CURRENCY_OPTIONS, DECIMAL_OPTIONS, MAX_TITLE, NUMBER_FORMAT_OPTIONS, cleanTitle, formatExample, measureFormatNote,
  valueLabelsApply,
} from './formatSettings';

export default function FormatPanel({ format, autoTitle, type, onChange, measures = [], fieldsByKey = {} }) {
  const set = (patch) => onChange({ ...format, ...patch });
  const typed = Boolean(format.title.trim());
  const labels = valueLabelsApply(type);
  const note = measureFormatNote(format, measures, fieldsByKey);      // 13c-4: measures with their own format

  return (
    <div className="flex flex-col">
      <PanelLabel as="label" htmlFor="builder-title">Title</PanelLabel>
      <input
        id="builder-title" type="text" value={format.title} maxLength={MAX_TITLE}
        placeholder={autoTitle} onChange={(e) => set({ title: cleanTitle(e.target.value) })}
        className="h-9 w-full rounded-xl bg-[var(--ui-inset)] px-3 text-[13px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
      />
      <PanelHint>
        {typed ? (
          <>Your title. <button type="button" onClick={() => set({ title: '' })} className="underline hover:text-[var(--ui-text)]">Use the automatic title</button></>
        ) : 'Automatic: follows the fields until you type one.'}
      </PanelHint>

      <PanelLabel id="builder-number-format-label">Numbers</PanelLabel>
      <Select value={format.number_format} onValueChange={(v) => set({ number_format: v })}>
        <SelectTrigger aria-labelledby="builder-number-format-label" aria-label="Number format"><SelectValue /></SelectTrigger>
        <SelectContent>
          {NUMBER_FORMAT_OPTIONS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>

      <div className="mt-2 grid grid-cols-2 gap-2">
        {format.number_format === 'currency' && (
          <Select value={format.currency} onValueChange={(v) => set({ currency: v })}>
            <SelectTrigger aria-label="Currency"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={format.decimals == null ? 'auto' : String(format.decimals)}
          onValueChange={(v) => set({ decimals: v === 'auto' ? null : Number(v) })}>
          <SelectTrigger aria-label="Decimal places"
            className={format.number_format === 'currency' ? '' : 'col-span-2'}><SelectValue /></SelectTrigger>
          <SelectContent>
            {DECIMAL_OPTIONS.map((d) => (
              <SelectItem key={d} value={d}>{d === 'auto' ? 'Auto decimals' : `${d} decimal${d === '1' ? '' : 's'}`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <PanelHint>
        {note || formatExample(format)}
        {!note && format.number_format === 'percent' && '. Values are ratios: 0.25 shows as 25%'}
      </PanelHint>

      <PanelLabel id="builder-labels-label">Value labels</PanelLabel>
      <ToggleGroup type="single" aria-labelledby="builder-labels-label" disabled={!labels}
        value={labels && format.value_labels ? 'on' : 'off'} onValueChange={(v) => v && set({ value_labels: v === 'on' })}>
        <ToggleGroupItem value="off">Off</ToggleGroupItem>
        <ToggleGroupItem value="on">On</ToggleGroupItem>
      </ToggleGroup>
      <PanelHint>
        {!labels ? 'Bar, line, area and donut charts show value labels.'
          : ['doughnut', 'pie', 'polarArea'].includes(type) ? 'Each slice is named with its value and share of the total.'
            : 'A number on every bar or point, so a zero bar is visible too.'}
      </PanelHint>
    </div>
  );
}
